import { api } from "@/shared/api";
import type { OrgScopeParams } from "@/shared/hooks/useOrgScopeFilter";

// local-only: drift D-16 — generated CustomerInfo marks all fields optional, handwritten
// requires explicit-null fields (runtime contract). V24 reconcile via
// 2026-05-17-fe-drift-cs-session.md proposal.
export type CustomerInfo = {
  externalUserid: string | null;
  nickname: string | null;
  avatar: string | null;
  /** 0=未知 1=男 2=女 */
  gender: number | null;
  unionid: string | null;
  lastSyncedAt: string | null;
};

// local-only: drift D-17 — generated CsSessionDto narrows channel/status to enum unions
// (channel: "wecom"|"miniapp"|"web"; status: "ACTIVE"|"CLOSED"|"TRANSFERRED"|"HUMAN_HANDLING")
// while handwritten uses plain string (backend may return more values); customer is
// optional-undefined in generated vs required-nullable here. V24 reconcile via
// 2026-05-17-fe-drift-cs-session.md proposal.
export type CsSession = {
  id: number;
  sessionId: string;
  tenantCode: string;
  channel: string;
  status: string;
  visitorName: string | null;
  externalUserid: string | null;
  kfName: string | null;
  createdAt: string;
  /** 企微客服渠道才有；null = 客户档案未抓取或非企微来源(网页 / H5 访客没有企微档案)。 */
  customer: CustomerInfo | null;
  /** V22 (2026-05-15) — 当前 seat 用户 id + display_name, MANAGER+ 看全租户时显示"谁接管的"。 */
  seatUserId?: number | null;
  seatUserName?: string | null;
  /** 1010 契约 §3:web_widget 会话的接入点名(如「官网」);其它渠道不回。 */
  entryName?: string | null;
};

// local-only: drift D-18 — generated CsSessionDetailDto extends CsSessionDto with messages[];
// handwritten omits messages and adds seatId/closedAt (backend returns both shapes via
// different endpoints). V24 reconcile via 2026-05-17-fe-drift-cs-session.md proposal.
export type CsSessionDetail = {
  id: number;
  sessionId: string;
  tenantCode: string;
  channel: string;
  status: string;
  visitorName: string | null;
  externalUserid: string | null;
  kfName: string | null;
  seatId: number | null;
  createdAt: string | null;
  closedAt: string | null;
  customer: CustomerInfo | null;
  /** 1010:web_widget 会话的接入点名(与列表一致);其它渠道不回。 */
  entryName?: string | null;
  /** 1010 一期补完契约 6:接管人显示名(所有渠道);没人接管 = 空。 */
  assigneeName?: string | null;
  /** 1010 一期补完契约 6:网页 / H5 访客信息(仅 web_widget);手机号后端已脱敏。 */
  visitor?: CsVisitorInfo | null;
};

// local-only: 1010 一期补完契约 6 —— CsSessionDetailDto.visitor(仅 web_widget)。
export type CsVisitorInfo = {
  entryName?: string | null;
  landingPageUrl?: string | null;
  landingPageTitle?: string | null;
  /** 已脱敏,如 138****8000 */
  leadPhone?: string | null;
  leadName?: string | null;
  leadWechat?: string | null;
  leadNote?: string | null;
  leadAt?: string | null;
};

// local-only: backend schema gap — GET /sessions/{id}/servicers response item not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-cs.md proposal.
export type Servicer = {
  userid: string;
  status: number | null;
};

// local-only: backend schema gap — feedback signal enum not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-cs.md proposal.
export type FeedbackSignal = "up" | "down";

// local-only: backend schema gap — message feedback summary DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-cs.md proposal.
export type FeedbackSummary = {
  overall: FeedbackSignal | null;
  perSourceByDocId: Record<string, FeedbackSignal>;
};

// local-only: backend schema gap — getMessageDetail.sourceChunks[] item not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-cs.md proposal.
export type SourceChunk = {
  chunkIndex: number | null;
  docId: string;
  /** QA 类型：问题文本；其他类型：chunk 原文 */
  content: string;
  /** 仅 QA 类型有值 —— 该问题对应的答案；其他类型为 null */
  answer?: string | null;
  pageNumber?: number | null;
};

// local-only: drift D-19 — generated CsMessageDto uses sender/timestamp/messageId fields,
// handwritten uses messageRole/createdAt/sceneTag/feedbackSummary/sourceDocIds/handoverSuggested.
// Two distinct backend shapes (Spring DTO returned by list endpoint vs SSE message). V24
// reconcile via 2026-05-17-fe-drift-cs-session.md proposal.
/** 会话消息列表项 —— 不含 sourceChunks；命中片段通过 getMessageDetail 懒加载。 */
export type CsMessageListItem = {
  id: number;
  messageId: string | null;
  messageRole: string;
  messageType: string;
  content: string;
  sourceDocIds: string[];
  sceneTag: string | null;
  feedbackSummary: FeedbackSummary | null;
  handoverSuggested: boolean;
  createdAt: string | null;
};

// local-only: backend schema gap — getMessageDetail root response DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-cs.md proposal.
// local-only: backend schema gap — getMessageDetail.sources[] item not in spec.
/**
 * 这条答案**真正引用**的来源(E2 / design §7.4)。
 *
 * 与 `sourceChunks` 的区别正是审阅人要的:那个是「召回了什么」—— 检索 over-fetch,几乎不空,
 * 分不出哪几篇真被用上;本类型是 ai 侧核验过的真引用(模型编造的 chunk_id 已丢弃),
 * 带文档名 + 页码 + 原文片段,人能直接翻过去核对。
 */
export type AnswerSource = {
  docId: string;
  /** 人能读的文档名。合成 doc(公司事实/图)显示的是它**原本**的出处,不是 `__cfact__/1001`。 */
  docName: string;
  /** 第几页;null = 该格式没有页(txt/docx)或定位不到 —— 后端不会编一个出来。 */
  page?: number | null;
  /** 原文片段(逐字来自文档,非模型转述)。 */
  snippet?: string | null;
};

// local-only: backend schema gap — getMessageDetail.media[] item not in spec.
// 契约 proposal: docs/contracts/v23/proposals/2026-07-16-cs-answer-media-egress.md
/**
 * 这条答案**真发给客户**的一张图(G1a / design §4)。
 *
 * 为什么审计必须看得到:图是**另一条消息**发出去的,光看 answer 文本完全看不出 AI 给客户
 * 发过什么。出了事(「这张内部图怎么发出去的」)审计里得有痕迹可查。
 */
export type AnswerMedia = {
  /** MinIO object key —— 排查时的稳定标识(previewUrl 会过期,它不会)。 */
  assetRef: string;
  /** 一句话「这是啥」。 */
  caption: string;
  docId: string;
  /** product_sample / spec_table / qualification_cert / deliverable… */
  usageIntent: string;
  /** 短时预览 URL;null = 签不出来(对象已删/桶认不出)—— 但这条记录仍在,图发过是既成事实。 */
  previewUrl?: string | null;
};

/** 会话消息 detail —— 命中片段 + 真实引用来源 + 真发出去的图。 */
export type SessionMessageDetail = {
  sourceChunks: SourceChunk[];
  /** ai 给不出可核验引用时为空;E2 之前落的老消息也为空 —— 空 ≠ 出错。 */
  sources: AnswerSource[];
  /** 这条答案没吐图 / G1a 之前的老消息 → 空,不是出错。 */
  media: AnswerMedia[];
};

// local-only: backend schema gap — POST /sessions/{sid}/messages/{mid}/feedback body DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-cs.md proposal.
export type FeedbackSubmitBody = {
  overall?: FeedbackSignal;
  perSource?: { docId: string; chunkIndex?: number; signal: FeedbackSignal }[];
};

// local-only: drift D-20 — generated CsUnmatchedQuestionDto only has id/question/count/status
// (4 fields) while handwritten covers 9 (sessionId/hitCount/firstSeenAt/lastSeenAt/sampleAnswer/
// source). V24 reconcile via 2026-05-17-fe-drift-cs-session.md proposal.
export type CsUnmatchedQuestion = {
  id: number;
  question: string;
  sessionId: string;
  status: string;
  hitCount: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  sampleAnswer: string;
  source: string;
  /** 1010 一期补完契约 7:会话渠道(wecom_kf / web_widget);老数据 / 老后端不回。 */
  channel?: string | null;
};

// local-only: frontend query params builder — flattened into URLSearchParams in listSessions.
export type CsSessionListParams = {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
  channel?: string;
  from?: string;
  to?: string;
  /** V22 (2026-05-15) — MANAGER+ 切"只看我的"接管会话(legacy;#9 起用 scope 统一) */
  mine?: boolean;
} & OrgScopeParams;

// local-only: backend schema gap — common PageResponse<T> generic not parameterized in spec.
// See posterService.ts PageResp note. V24 yaml align.
export type PageResponse<T> = {
  records: T[];
  total: number;
  page: number;
  size: number;
};

// local-only: backend schema gap — GET /tenant/customer-service/stats response DTO not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-cs.md proposal.
export type CsStats = {
  todaySessionCount: number;
  totalSessionCount: number;
  activeSessionCount: number;
  humanHandlingCount: number;
  unmatchedQuestionCount: number;
  knowledgeDocCount: number;
  totalChunks: number;
  /** 接管率 = 今日 human_handling / 今日会话；分母为 0 时为 null */
  todayHumanTakeoverRate: number | null;
  /** 未命中率 = 今日新增未命中 / 今日 AI 助手消息；分母为 0 时为 null */
  todayUnmatchedRate: number | null;
  /** 点赞率 = 正向 / (正向 + 负向)；分母为 0 时为 null */
  todayPositiveFeedbackRate: number | null;
  todayAssistantMessageCount: number;
  todayUnmatchedCount: number;
  todayFeedbackPositive: number;
  todayFeedbackNegative: number;
};

// local-only: backend schema gap — GET /risk-alerts response item not in spec.
// V24 yaml align via 2026-05-17-fe-generated-coverage-cs.md proposal.
export type RiskAlert = {
  message: string;
  level: "warning" | "error";
  category: string;
};

/**
 * 客服配额当天耗尽状态(0926 修复波第五批,契约由后端代理 O 定)。
 * GET /tenant/customer-service/quota-status —— 所有租户角色可读,前端只对 TENANT_ADMIN 展示。
 * 只统计当天;AI 成功回复一次后 exhausted 自动回 false。时间为带 +08:00 的 ISO 串。
 */
export type CsQuotaStatus = {
  exhausted: boolean;
  /** yyyy-MM-dd(当天) */
  date: string;
  firstExhaustedAt: string | null;
  lastExhaustedAt: string | null;
  /** 当天因配额用完被跳过(AI 未回复)的客户消息数 */
  skippedMessages: number;
  /** 后端给的人话提示;null 时前端用兜底句 */
  message: string | null;
};

export const aiKefuService = {
  getStats: () => api.get<CsStats>("/tenant/customer-service/stats"),
  getQuotaStatus: () => api.get<CsQuotaStatus>("/tenant/customer-service/quota-status"),
  listSessions: (params: CsSessionListParams = {}) => {
    const qs = new URLSearchParams();
    if (params.page !== undefined) qs.set("page", String(params.page));
    if (params.size !== undefined) qs.set("size", String(params.size));
    if (params.search) qs.set("search", params.search);
    if (params.status && params.status !== "all") qs.set("status", params.status);
    if (params.channel && params.channel !== "all") qs.set("channel", params.channel);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.mine) qs.set("mine", "true");
    // #9 组织级归属筛选(后端按接管坐席 assignee 归属过滤;scope=all 不下推 = 全租户共享读)。
    if (params.scope && params.scope !== "all") qs.set("scope", params.scope);
    if (params.departmentId != null) qs.set("departmentId", String(params.departmentId));
    if (params.memberId != null) qs.set("memberId", String(params.memberId));
    const suffix = qs.toString();
    return api.get<PageResponse<CsSession>>(
      `/tenant/customer-service/sessions${suffix ? `?${suffix}` : ""}`
    );
  },
  getSessionDetail: (sessionId: string) =>
    api.get<CsSessionDetail>(`/tenant/customer-service/sessions/${sessionId}`),
  getSessionMessages: (sessionId: string, page = 1, size = 50) =>
    api.get<PageResponse<CsMessageListItem>>(
      `/tenant/customer-service/sessions/${sessionId}/messages?page=${page}&size=${size}`
    ),
  getMessageDetail: (sessionId: string, messageId: string) =>
    api.get<SessionMessageDetail>(
      `/tenant/customer-service/sessions/${sessionId}/messages/${encodeURIComponent(messageId)}/detail`
    ),
  // 全量返回(可选 status 过滤);优化 Tab 顶部 hot TOP5(含已处理)+ 建议按 hitCount 跨全量取,分页在前端做。
  listUnmatchedQuestions: (params: { status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    const qs = q.toString();
    return api.get<CsUnmatchedQuestion[]>(
      `/tenant/customer-service/unmatched-questions${qs ? `?${qs}` : ""}`
    );
  },
  listRiskAlerts: () => api.get<RiskAlert[]>("/tenant/customer-service/risk-alerts"),
  updateUnmatchedQuestionStatus: (id: number, status: string) =>
    api.post<void>(`/tenant/customer-service/unmatched-questions/${id}/status`, { status }),
  /** 未命中清单 CSV 导出(运营整理成 QA 文档走正常上传补 KB;KB 单一来源不开第二写入口)。 */
  exportUnmatchedQuestions: async () => {
    const res = await api.getBlob("/tenant/customer-service/unmatched-questions/export");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "unmatched-questions.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  listServicers: (sessionId: string) =>
    api.get<Servicer[]>(
      `/tenant/customer-service/sessions/${encodeURIComponent(sessionId)}/servicers`
    ),
  /**
   * 接管。企微会话必须带 servicerUserid(企微接待人员);网页 / H5 等其它渠道不带 ——
   * 后端忽略该字段,接管人 = 当前登录人,并给访客落一条「人工客服 xx 已接入」(1010 一期补完契约 1)。
   */
  takeoverSession: (sessionId: string, servicerUserid?: string) =>
    api.post<void>(
      "/tenant/customer-service/sessions/takeover",
      servicerUserid ? { sessionId, servicerUserid } : { sessionId }
    ),
  closeSession: (sessionId: string) =>
    api.post<void>("/tenant/customer-service/sessions/close", { sessionId }),
  /** 后台手动从企业微信拉一次最新的 nickname/avatar/gender 写入 cs_customers，返回最新值(仅企微客服会话可用)。 */
  refreshCustomer: (sessionId: string) =>
    api.post<CustomerInfo>(
      `/tenant/customer-service/sessions/${encodeURIComponent(sessionId)}/refresh-customer`,
      {}
    ),
  sendHumanReply: (sessionId: string, content: string, markAsBenchmark = false) =>
    api.post<CsMessageListItem>("/tenant/customer-service/sessions/reply", {
      sessionId,
      content,
      markAsBenchmark,
    }),
  submitFeedback: (sessionId: string, messageId: string, body: FeedbackSubmitBody) =>
    api.post<FeedbackSummary>(
      `/tenant/customer-service/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`,
      body
    ),
};
